import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { formatCurrency, formatDate, formatTime } from '@/lib/utils';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';

export function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const { data: booking, isLoading } = useQuery({
    queryKey: ['booking', id],
    queryFn: () => api.bookings.get(id!),
    enabled: !!id,
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.bookings.cancel(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking', id] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      toast.success('Booking cancelled successfully');
      setShowCancelConfirm(false);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to cancel booking');
    },
  });

  if (isLoading) {
    return (
      <div className="container-narrow py-8">
        <Skeleton className="h-8 w-64 mb-8" />
        <Card>
          <Skeleton className="h-64" />
        </Card>
      </div>
    );
  }

  if (!booking?.data) {
    return (
      <div className="container-narrow py-16 text-center">
        <h1 className="text-2xl font-bold text-sand-900">Booking not found</h1>
        <Link to="/bookings" className="mt-8 inline-block">
          <Button>View My Bookings</Button>
        </Link>
      </div>
    );
  }

  const { data } = booking;
  const canCancel = ['pending', 'confirmed'].includes(data.status);

  return (
    <div className="bg-sand-50 min-h-screen py-8">
      <div className="container-narrow">
        <Link to="/bookings" className="inline-flex items-center gap-2 text-sm text-sand-500 hover:text-sand-700 mb-6">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to bookings
        </Link>

        <Card>
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-sand-900">{data.course?.name}</h1>
              <p className="text-sand-500">{data.course?.address}</p>
              <p className="text-sand-500">{data.course?.city}, {data.course?.state} {data.course?.zipCode}</p>
            </div>
            <Badge
              variant={
                data.status === 'confirmed' || data.status === 'checked_in'
                  ? 'sage'
                  : data.status === 'cancelled' || data.status === 'no_show'
                  ? 'error'
                  : 'sand'
              }
            >
              {data.status.replace('_', ' ')}
            </Badge>
          </div>

          {/* Confirmation Code */}
          <div className="rounded-xl bg-sand-50 p-4 mb-6 text-center">
            <p className="text-sm text-sand-500 mb-1">Confirmation Code</p>
            <p className="text-2xl font-bold tracking-wider text-sand-900">{data.confirmationCode}</p>
          </div>

          {/* Tee Time Details */}
          <div className="grid gap-4 sm:grid-cols-3 mb-6">
            <div className="rounded-xl bg-sand-50 p-4">
              <p className="text-sm text-sand-500">Date</p>
              <p className="text-lg font-semibold text-sand-900">{formatDate(data.teeTime?.date)}</p>
            </div>
            <div className="rounded-xl bg-sand-50 p-4">
              <p className="text-sm text-sand-500">Time</p>
              <p className="text-lg font-semibold text-sand-900">{formatTime(data.teeTime?.time)}</p>
            </div>
            <div className="rounded-xl bg-sand-50 p-4">
              <p className="text-sm text-sand-500">Players</p>
              <p className="text-lg font-semibold text-sand-900">{data.playerCount}</p>
            </div>
          </div>

          {/* Players */}
          {data.players && data.players.length > 0 && (
            <div className="mb-6">
              <h2 className="font-semibold text-sand-900 mb-3">Players</h2>
              <div className="space-y-2">
                {data.players.map((player: any) => (
                  <div key={player.id} className="flex items-center gap-3 p-3 rounded-lg bg-sand-50">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-sage-100 text-sm font-medium text-sage-700">
                      {player.firstName.charAt(0)}{player.lastName.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-sand-900">{player.firstName} {player.lastName}</p>
                      {player.handicap && <p className="text-sm text-sand-500">HCP: {player.handicap}</p>}
                    </div>
                    {player.isGuest && <Badge variant="sand">Guest</Badge>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pricing */}
          <div className="border-t border-sand-100 pt-6">
            <h2 className="font-semibold text-sand-900 mb-3">Payment Details</h2>
            <div className="space-y-2">
              <div className="flex justify-between text-sand-700">
                <span>Subtotal</span>
                <span>{formatCurrency(data.subtotal)}</span>
              </div>
              {data.discountAmount > 0 && (
                <div className="flex justify-between text-sage-600">
                  <span>Discount</span>
                  <span>-{formatCurrency(data.discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sand-700">
                <span>Tax</span>
                <span>{formatCurrency(data.taxAmount)}</span>
              </div>
              <hr className="border-sand-200" />
              <div className="flex justify-between text-lg font-semibold text-sand-900">
                <span>Total</span>
                <span>{formatCurrency(data.totalAmount)}</span>
              </div>
            </div>
          </div>

          {/* Special Requests */}
          {data.specialRequests && (
            <div className="border-t border-sand-100 pt-6 mt-6">
              <h2 className="font-semibold text-sand-900 mb-2">Special Requests</h2>
              <p className="text-sand-600">{data.specialRequests}</p>
            </div>
          )}

          {/* Actions */}
          {canCancel && (
            <div className="border-t border-sand-100 pt-6 mt-6">
              {!showCancelConfirm ? (
                <Button
                  variant="outline"
                  onClick={() => setShowCancelConfirm(true)}
                  className="text-terracotta-600 border-terracotta-200 hover:bg-terracotta-50"
                >
                  Cancel Booking
                </Button>
              ) : (
                <div className="rounded-xl bg-terracotta-50 p-4">
                  <p className="text-terracotta-800 font-medium mb-2">Are you sure you want to cancel this booking?</p>
                  <p className="text-sm text-terracotta-600 mb-4">This action cannot be undone.</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowCancelConfirm(false)}
                    >
                      Keep Booking
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => cancelMutation.mutate()}
                      isLoading={cancelMutation.isPending}
                      className="bg-terracotta-600 hover:bg-terracotta-700"
                    >
                      Yes, Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
